import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getBiometricDevices } from "../../services/biometric-devices";
import ConnectorPreview from "./ConnectorPreview";
import AttendanceProcessingQueue from "./AttendanceProcessingQueue";
import "./PunchReview.css";
import { FiFilter, FiRefreshCw, FiArrowRight } from "react-icons/fi";

export default function PunchReview() {
  const { accessToken } = useAuth();
  const [devices, setDevices] = useState([]),
    [selected, setSelected] = useState(""),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setBusy(true);
    setDevices([]);
    setSelected("");
    setError("");
    getBiometricDevices(accessToken)
      .then((result) => {
        if (!active) return;
        const list = Array.isArray(result)
          ? result
          : result?.items || result?.data || [];
        setDevices(list);
        const requested = new URLSearchParams(window.location.search).get(
          "device",
        );
        setSelected(list.some((d) => d.id === requested) ? requested : "");
      })
      .catch((e) => {
        if (active) setError(e.message || "Unable to load machines");
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);
  const device = devices.find((d) => d.id === selected);
  return (
    <main className="punch-review space-y-6">
      <header className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div><h1 className="text-3xl font-bold text-gray-900">Punch Review & Approval</h1>
        <p className="mt-2 text-gray-500">Review machine punches, historical attendance and work-session exceptions.</p></div>
        <a href="/admindashboard/attendance" className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-800 px-5 py-3 font-medium text-white shadow-sm transition-all hover:bg-red-900">Attendance Overview <FiArrowRight aria-hidden="true" size={18}/></a>
      </header>
      <div className="pr-policy-grid">
        <article className="pr-policy">
          <span className="pr-eyebrow">28 OCT 2026 ONWARD</span>
          <h2>Automatic work sessions</h2>
          <p>
            Mapped, valid punches flow to attendance when server automation is
            enabled.
          </p>
        </article>
        <article className="pr-policy">
          <span className="pr-eyebrow">BEFORE 28 OCTOBER</span>
          <h2>Review, then approve</h2>
          <p>
            Check historical dates and employee links before creating
            attendance.
          </p>
        </article>
        <article className="pr-policy">
          <span className="pr-eyebrow">EXCEPTIONS</span>
          <h2>Keep HR in control</h2>
          <p>
            Missing OUT punches and conflicts stay visible. Manual attendance is
            protected.
          </p>
        </article>
      </div>
      <section className="overflow-visible rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 rounded-t-2xl border-b border-gray-100 px-5 py-4"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-800 text-white shadow-sm"><FiFilter aria-hidden="true" size={18}/></div><div><h2 className="font-semibold text-gray-900">Find machine punches</h2><p className="text-sm text-gray-500">Select a machine to inspect employee punches and India dates.</p></div></div>
        <div className="flex flex-wrap items-end gap-4 p-5">
        <label className="min-w-64 flex-1 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
          Attendance machine
          <select
            aria-label="Attendance machine"
            className="mt-1.5 block w-full rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm font-normal normal-case tracking-normal text-gray-900"
            value={selected}
            disabled={busy}
            onChange={(e) => {
              setSelected(e.target.value);
              setRefresh(0);
            }}
          >
            <option value="">
              {busy ? "Loading machines…" : "Choose an attendance machine"}
            </option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} · {d.code}
                {d.isActive === false ? " · Inactive" : ""}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary-200 bg-white px-4 py-3 text-sm font-semibold text-primary-800 transition hover:bg-primary-50"
          disabled={!device || busy}
          onClick={() => setRefresh((v) => v + 1)}
        >
          <FiRefreshCw aria-hidden="true" size={16}/>Refresh uploaded punches
        </button>
        </div>
      </section>
      {device && (
        <div className="pr-machine-context">
          <span
            className={`pr-dot ${device.isActive === false ? "pr-dot-inactive" : ""}`}
          />
          <strong>{device.name}</strong>
          <span>{device.code}</span>
          <span className="pr-context-note">
            {device.isActive === false
              ? "Machine registration inactive"
              : "Machine registration active · connection health is shown separately"}
          </span>
        </div>
      )}
      {error && (
        <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-800">
          {error}
        </p>
      )}
      {!busy && !error && !devices.length && (
        <p className="rounded-xl border p-6">
          No face machines are configured. Add a machine and upload preview
          punches first.
        </p>
      )}
      {!device && !busy && devices.length > 0 && (
        <div className="pr-empty">
          <span className="pr-eyebrow">START YOUR REVIEW</span>
          <h2>Select an attendance machine</h2>
          <p>
            Choose a machine above to see its processing queue, inspect punch
            pairs and review historical dates.
          </p>
        </div>
      )}
      {device && (
        <>
          <AttendanceProcessingQueue
            key={`queue:${device.id}:${accessToken}`}
            deviceId={device.id}
            accessToken={accessToken}
            refresh={refresh}
          />
          <ConnectorPreview
            key={`${device.id}:${accessToken}`}
            device={device}
            accessToken={accessToken}
            refresh={refresh}
            approvalPage
            initialUserId={
              new URLSearchParams(window.location.search).get("user") || ""
            }
            initialDate={
              new URLSearchParams(window.location.search).get("date") || ""
            }
          />
        </>
      )}
    </main>
  );
}
